import CircularProgressCustom from '@orientaction/components/CircularProgressCustom';
import { appLanguage } from '@orientaction/reducers';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import VariableDetailsModal from '../components/DetailModal/NeedsDetailModal';
import {
  persoModalStaticBlockElement,
  proModalStaticBlockElement,
} from '../../../../../PageResultat/NeedsResult/VariableDetailsModal/constant';
import RedesignImage from '../../../../shared/components/RedesignImage';
import ResultDetailCard from '../components/ResultDetailCard';
import { DetailsSection, SummaryCard, SummaryGrid } from '../components/SummarySection';
import { useMajorResultStyles } from '../MajorResult.styles';
import { MajorTestContentProps } from '../types';
import { useNeedsContentStyles } from './NeedsContent.styles';
import { needsAssets, needsRankColors } from './needsPresentation';

const getNeedsPyramidImage = (key: string): string => {
  switch (key) {
    case 'physiological_pro':
    case 'physiological_perso':
      return '/images/need/Physiologique.png';
    case 'security_pro':
    case 'security_perso':
      return '/images/need/en_security.png';
    case 'horizontal_belonging_pro':
    case 'horizontal_belonging_perso':
      return '/images/need/en_appartenance.png';
    case 'vertical_belonging_pro':
    case 'vertical_belonging_perso':
      return '/images/need/en_appartenance_V.png';
    case 'accomplishment_pro':
    case 'accomplishment_perso':
      return '/images/need/en_Accomplissement.png';
    case 'self_realization_pro':
    case 'self_realization_perso':
      return '/images/need/en_realisation_soi.png';
    case 'spirituality_pro':
    case 'spirituality_perso':
      return '/images/need/en_spiritualite.png';
    case 'eternity_pro':
    case 'eternity_perso':
      return '/images/need/en_eternity.png';
    default:
      return '';
  }
};

const isProVariable = (key?: string) => !key?.endsWith('_perso');

const getModalStaticBlock = (key?: string) =>
  isProVariable(key) ? proModalStaticBlockElement : persoModalStaticBlockElement;

const getPyramidSrc = (favorite: any, lang: string) =>
  lang !== 'FR' && favorite?.key
    ? getNeedsPyramidImage(favorite.key)
    : needsAssets[favorite?.key]?.pyramid || favorite?.imgPyramid || '';

type NeedCardsProps = {
  variables: any[];
  onLearnMore: (variable: any) => void;
};

const NeedCards = ({ variables, onLearnMore }: NeedCardsProps) => {
  const classes = useMajorResultStyles();

  return (
    <>
      {variables.map((variable: any, index: number) => {
        const imageSrc = needsAssets[variable.key]?.image || variable.path;
        const color = needsRankColors[index % needsRankColors.length];
        const score = (variable.score * 100) / 125;

        return (
          <ResultDetailCard
            key={variable.key}
            label={variable.label}
            color={color}
            imageSrc={imageSrc}
            imageAlt={variable.label}
            onLearnMore={() => onLearnMore({ ...variable, color })}
            imageOverlay={
              <div className={classes.scoreBadge}>
                <CircularProgressCustom
                  value={variable.score}
                  color={color}
                  size={78}
                  fontSize={18}
                  fontSizeFloat={12}
                  totalValue={125}
                  calculatedValue={score}
                />
              </div>
            }
          />
        );
      })}
    </>
  );
};

const NeedsContent: FC<MajorTestContentProps> = ({ myResult }) => {
  const classes = useMajorResultStyles();
  const local = useNeedsContentStyles();
  const lang: string = useSelector(appLanguage);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<any>(null);

  const proFavorite = myResult?.pro?.favorite;
  const persoFavorite = myResult?.perso?.favorite;
  const proVariables = myResult?.pro?.treeMax || [];
  const persoVariables = myResult?.perso?.treeMax || [];
  const proPyramidSrc = getPyramidSrc(proFavorite, lang);
  const persoPyramidSrc = getPyramidSrc(persoFavorite, lang);

  const openModal = (variable: any) => {
    setSelectedVariable(variable);
    setModalOpen(true);
  };

  return (
    <>
      <div className={local.summary}>
        <SummaryCard title="SYNTHÈSE DE VOS RÉSULTATS">
          <div className={local.pyramids}>
            <div className={local.pyramidColumn}>
              {proPyramidSrc && (
                <RedesignImage
                  priority={true}
                  src={proPyramidSrc}
                  alt="Pyramide des besoins professionnels"
                  className={local.pyramid}
                />
              )}
              <p>Voici votre besoin principal dans votre vie professionnelle</p>
              {proFavorite?.label && (
                <p className={`${classes.favoriteLabel} ${local.favorite}`}>{proFavorite.label}</p>
              )}
              <p>
                Voici votre besoin principal dans votre vie professionnelle. La satisfaction de vos
                besoins est à la source de votre bien-être et de votre motivation. Il est important
                que votre métier, vos conditions de travail et de rémunération, ainsi que vos
                relations professionnelles contribuent pleinement à satisfaire ce besoin.
              </p>
            </div>
            <div className={local.pyramidColumn}>
              {persoPyramidSrc && (
                <RedesignImage
                  priority={true}
                  src={persoPyramidSrc}
                  alt="Pyramide des besoins personnels"
                  className={local.pyramid}
                />
              )}
              <p>Voici votre besoin principal dans votre vie personnelle</p>
              {persoFavorite?.label && (
                <p className={`${classes.favoriteLabel} ${local.favorite}`}>
                  {persoFavorite.label}
                </p>
              )}
              <p>
                Voici votre besoin principal dans votre vie personnelle. La satisfaction de vos
                besoins est à la source de votre bien-être et de votre motivation. Il est important
                que votre situation personnelle, vos conditions de vie, ainsi que vos relations
                conjugales, amicales et familiales contribuent pleinement à satisfaire ce besoin.
              </p>
            </div>
          </div>
        </SummaryCard>
      </div>

      <SummaryGrid>
        <SummaryCard title="APPLICATIONS CONCRÈTES DANS VOTRE VIE PROFESSIONNELLE">
          <div className={local.application}>
            <p>
              Si vous ne vous sentez pas pleinement satisfait(e) dans votre vie professionnelle,
              c’est sans doute parce que certains de vos besoins ne sont pas satisfaits. Dans le
              domaine professionnel, cela peut être d’avoir une rémunération que vous jugez
              insuffisante, d’avoir des relations de mauvaise qualité avec vos collègues ou encore
              de ne pas avoir un poste suffisamment stimulant par rapport à vos attentes.
            </p>
            <p>
              Tous ces éléments risquent d’entraîner de la démotivation. Au contraire, si vos
              besoins sont satisfaits, alors vous vous sentirez pleinement motivé(e) par votre
              travail. Voilà pourquoi il faut être à l’écoute de vos besoins et être capable de les
              expliciter à votre manager. Cela lui permettra de tenter d’y répondre. Vous vous
              sentirez compris(e) et vous aurez le sentiment de progresser…
            </p>
          </div>
        </SummaryCard>
        <SummaryCard title="APPLICATIONS CONCRÈTES DANS VOTRE VIE PERSONNELLE">
          <div className={local.application}>
            <p>
              Si vous ne vous sentez pas pleinement épanoui(e) dans votre vie personnelle, c’est
              sans doute parce que certains de vos besoins demeurent insatisfaits. Lorsque l’un de
              nos besoins demeure insatisfait, nous nous sentons frustrés. Cette frustration se
              manifeste par une certaine contrariété, des plaintes, voire de la colère qui peut être
              à la source de tensions, voire de véritables disputes avec vos proches. Au contraire,
              si vos besoins sont satisfaits, alors vos relations s’en trouveront renforcées. Voilà
              pourquoi il faut être à l’écoute de ses besoins et être capable de les expliciter à
              ses proches. Cela leur permettra de mieux y répondre et vous vous sentirez beaucoup
              mieux et plus épanoui(e) dans vos relations interpersonnelles. Satisfaire ses besoins
              est l’une des clefs du bonheur.
            </p>
          </div>
        </SummaryCard>
      </SummaryGrid>

      <DetailsSection title="DÉCOUVREZ VOS 3 BESOINS PROFESSIONNELS PRINCIPAUX">
        <NeedCards variables={proVariables} onLearnMore={openModal} />
      </DetailsSection>

      <DetailsSection title="DÉCOUVREZ VOS 3 BESOINS PERSONNELS PRINCIPAUX">
        <NeedCards variables={persoVariables} onLearnMore={openModal} />
      </DetailsSection>

      <VariableDetailsModal
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedVariable(null);
        }}
        variable={selectedVariable}
        modalStaticBlockElement={getModalStaticBlock(selectedVariable?.key)}
        isPro={isProVariable(selectedVariable?.key)}
      />
    </>
  );
};

export default NeedsContent;
